import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { UserQuota } from "@/types";

const FREE_LIMIT = 3;

interface UseQuotaReturn {
  quota: UserQuota | null;
  loading: boolean;
  canRecord: boolean;
  meetingsLeft: number;
  refresh: () => Promise<void>;
}

export function useQuota(): UseQuotaReturn {
  const [quota, setQuota] = useState<UserQuota | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from("user_quotas")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    setQuota(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isPro = quota?.plan === "pro" || quota?.plan === "team";
  const used = quota?.meetings_this_month ?? 0;
  const canRecord = isPro || used < FREE_LIMIT;
  const meetingsLeft = isPro ? Infinity : Math.max(0, FREE_LIMIT - used);

  return { quota, loading, canRecord, meetingsLeft, refresh };
}
